import { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router';
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Wind, Droplets, Gauge, CheckCircle2, AlertCircle, ShieldAlert,
  ArrowDown, Save, Beaker, FlaskConical, Thermometer, Timer,
  TrendingUp, BarChart3,
} from "lucide-react";
import { useSampleLoader } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

/* ─── Types ─────────────────────────────────────────────── */
interface AerationColumnParams {
  flowRate: number;        // m³/h
  influentFe: number;      // mg/L (Fe²⁺ dissolved)
  influentMn: number;      // mg/L (Mn²⁺ dissolved)
  influentH2S: number;     // mg/L
  ph: number;
  alkalinity: number;      // mg/L CaCO₃
  temperature: number;     // °C
  // Column geometry
  columnDiameter: number;  // m
  columnHeight: number;    // m (packed bed)
  freeboard: number;       // m
  packingType: 'tellerette' | 'pall_ring' | 'random_polypropylene' | 'tray_cascade';
  packingSize: number;     // mm nominal
  // Air supply
  airToWaterRatio: number; // Nm³ air per m³ water
  airPressure: number;     // bar gauge (blower delivery)
}

interface AerationColumnSize {
  area: number;            // m²
  superficialVelocity: number; // m/h water
  ebct: number;            // s contact time (empty bed)
  airFlow: number;         // Nm³/h
  blowerPower: number;     // kW
  packingVolume: number;   // m³
  // Performance
  feOxidationPct: number;  // % Fe²⁺ → Fe³⁺
  mnOxidationPct: number;  // %
  h2sStripPct: number;     // % H₂S stripped
  outletFe: number;        // mg/L remaining dissolved
  outletMn: number;
  outletH2S: number;
  doAchieved: number;      // mg/L dissolved O₂ after column
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'overdesigned';
  statusDetail: string;
}

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

/* ─── Calc engine ────────────────────────────────────────── */
export function calcAerationColumn(p: AerationColumnParams): AerationColumnSize {
  const area = Math.PI * Math.pow(p.columnDiameter / 2, 2);
  const superficialVelocity = p.flowRate / area; // m/h
  const ebct = (area * p.columnHeight / p.flowRate) * 3600; // s
  const airFlow = p.flowRate * p.airToWaterRatio;
  // Blower power: P = Q × ΔP / (η × 1000) — adiabatic approx
  const blowerPower = (airFlow * p.airPressure * 1e5) / (3600 * 1000 * 0.65);
  const packingVolume = area * p.columnHeight;

  // ─── Fe oxidation kinetics (Stumm & Lee, 1961) ───
  // d[Fe²⁺]/dt = -k × [Fe²⁺] × [O₂] × [OH⁻]²
  // Half-life at pH 7.8 ≈ 1–2 min with adequate DO
  // Simplified first-order at fixed pH:
  const phFactor = Math.max(0, Math.pow(10, (p.ph - 7) * 2)); // doubling per pH unit (squared OH dependence ≈ 100×/unit, dampened)
  const kFe = 0.05 * phFactor; // s⁻¹ effective
  const feOxidationPct = Math.min(99, (1 - Math.exp(-kFe * ebct)) * 100);
  const outletFe = p.influentFe * (1 - feOxidationPct / 100);

  // ─── Mn oxidation kinetics (much slower than Fe at neutral pH) ───
  // Mn²⁺ needs pH >9 for autoxidation; below that requires catalytic surface (covered in Greensand stage)
  const kMn = 0.005 * Math.pow(10, Math.max(0, p.ph - 8) * 2);
  const mnOxidationPct = Math.min(40, (1 - Math.exp(-kMn * ebct)) * 100);
  const outletMn = p.influentMn * (1 - mnOxidationPct / 100);

  // ─── H₂S stripping (depends on G/L ratio and Henry's constant) ───
  // Henry's coefficient for H₂S ~0.4 mol/(L·atm) at 20°C → easy to strip
  const tempFactor = 1 + 0.025 * (p.temperature - 20);
  const ntu = (p.airToWaterRatio * 0.6) * tempFactor; // number of transfer units
  const h2sStripPct = Math.min(99, (1 - Math.exp(-ntu)) * 100);
  const outletH2S = p.influentH2S * (1 - h2sStripPct / 100);

  // ─── DO achieved (saturation × approach fraction) ───
  // Saturation O₂ at 20°C ≈ 9.1 mg/L, drops ~0.2/°C
  const doSat = 14.6 - 0.394 * p.temperature + 0.0076 * p.temperature ** 2;
  const doApproach = Math.min(0.95, ntu / (1 + ntu));
  const doAchieved = doSat * doApproach;

  // ─── Status ───
  let status: AerationColumnSize['status']; let statusDetail: string;
  if (feOxidationPct >= 95 && ebct >= 30 && superficialVelocity <= 80) {
    status = 'optimal';
    statusDetail = `Optimal. Fe oxidation ${feOxidationPct.toFixed(0)}% in ${ebct.toFixed(0)}s EBCT. DO ${doAchieved.toFixed(1)} mg/L.`;
  } else if (feOxidationPct >= 80 && superficialVelocity <= 120) {
    status = 'acceptable';
    statusDetail = `Acceptable. Fe oxidation ${feOxidationPct.toFixed(0)}%. Consider longer column or higher air ratio.`;
  } else if (feOxidationPct < 80 || ebct < 15) {
    status = 'undersized';
    statusDetail = `Undersized. Fe oxidation only ${feOxidationPct.toFixed(0)}%. Increase column height, diameter or air ratio.`;
  } else {
    status = 'overdesigned';
    statusDetail = `Overdesigned for current duty. Consider smaller diameter to save capex.`;
  }

  return {
    area, superficialVelocity, ebct, airFlow, blowerPower, packingVolume,
    feOxidationPct, mnOxidationPct, h2sStripPct,
    outletFe, outletMn, outletH2S, doAchieved,
    status, statusDetail,
  };
}

/* ─── Defaults: borehole pre-oxidation (Mylar Drungans baseline) ─── */
const DEFAULTS: AerationColumnParams = {
  flowRate: 24,
  influentFe: 0.47,
  influentMn: 0.05,
  influentH2S: 0,
  ph: 7.8,
  alkalinity: 84,
  temperature: 12,
  columnDiameter: 0.9,
  columnHeight: 2.5,
  freeboard: 0.5,
  packingType: 'tellerette',
  packingSize: 50,
  airToWaterRatio: 8,
  airPressure: 0.4,
};

/* ─── Slider component (matches Multimedia pattern) ─── */
function Slider({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: {
  label: string; value: number; min: number; max: number; step: number;
  unit: string; icon: any; color?: string; onChange: (v: number) => void; info?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
        </div>
        <span className="text-xs font-bold text-white tabular-nums">
          {(() => { const v = Number.isFinite(value) ? value : 0; return step < 0.1 ? v.toFixed(2) : step < 1 ? v.toFixed(1) : v.toFixed(0); })()}
          <span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: color, color, ['--p' as any]: `${Math.max(0, Math.min(100, ((value - min) / Math.max(0.0001, (max - min))) * 100)).toFixed(2)}%` }}
      />
      {info && <p className="text-[10px] text-slate-600">{info}</p>}
    </div>
  );
}

function GaugeBar({ value, max, label, unit, color }: { value: number; max: number; label: string; unit: string; color: string }) {
  const safeValue = Number.isFinite(value) ? value : 0;
  const safeMax = Number.isFinite(max) && max > 0 ? max : 1;
  const pct = Math.min(100, Math.max(0, (safeValue / safeMax) * 100));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">{label}</span>
        <span className="font-bold" style={{ color }}>{safeValue.toFixed(2)} {unit}</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

const LS_KEY = 'rb_aeration_column_spec';

/* ─── Page ─── */
export default function AerationColumnSpecPage() {
  const navigate = useNavigate();
  const sampleLoader = useSampleLoader();
  const [params, setParams] = useState<AerationColumnParams>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) return { ...DEFAULTS, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return DEFAULTS;
  });
  const [saved, setSaved] = useState(false);

  // Hydrate from sample if loaded
  useEffect(() => {
    const sp = sampleLoader.baseSampleParams as any;
    if (!sp) return;
    setParams(p => ({
      ...p,
      flowRate: sp.flowRate ? sp.flowRate / 24 : p.flowRate,
      influentFe: sp.fe ?? sp.iron ?? p.influentFe,
      influentMn: sp.mn ?? sp.manganese ?? p.influentMn,
      ph: sp.ph ?? p.ph,
      alkalinity: sp.alkalinity ?? p.alkalinity,
      temperature: sp.temp ?? p.temperature,
    }));
  }, [sampleLoader.baseSampleParams]);

  const size = useMemo(() => calcAerationColumn(params), [params]);
  const update = <K extends keyof AerationColumnParams>(k: K, v: AerationColumnParams[K]) => setParams(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(params)); setSaved(true); toast.success('Aeration column spec saved'); setTimeout(() => setSaved(false), 1500); }
    catch { toast.error('Save failed'); }
  };

  const statusColors: any = {
    optimal: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
    acceptable: { bg: 'bg-sky-500/5', border: 'border-sky-500/20', text: 'text-sky-400', icon: CheckCircle2 },
    undersized: { bg: 'bg-rose-500/5', border: 'border-rose-500/20', text: 'text-rose-400', icon: ShieldAlert },
    overdesigned: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', text: 'text-amber-400', icon: AlertCircle },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  const scienceSections = [
    {
      title: 'Fe²⁺ Oxidation Kinetics (Stumm & Lee 1961)',
      content: `d[Fe²⁺]/dt = -k · [Fe²⁺] · pO₂ · [OH⁻]² — second-order in OH⁻ means the rate is highly pH dependent: a 1-unit pH increase speeds Fe oxidation ~100×. At pH 7.8 and adequate DO, half-life is 1–2 min — well within the column EBCT of ${fmt(size.ebct, 0)}s. Below pH 7.0 the column needs chemical oxidation (NaOCl or KMnO₄ trim).`,
    },
    {
      title: 'Why Mn²⁺ needs a catalytic surface',
      content: `Mn²⁺ autoxidation is negligibly slow below pH 9. At pH 7.8 only ~${fmt(size.mnOxidationPct, 0)}% Mn oxidises in the column. Effective Mn removal requires a catalytic media surface (MnO₂-coated greensand or pyrolusite) downstream — covered in the catalytic filter stage.`,
    },
    {
      title: 'H₂S stripping (Henry\'s Law)',
      content: `H₂S has H ≈ 0.4 mol/(L·atm) at 20°C — easily stripped at modest air/water ratios. Current G/L = ${fmt(params.airToWaterRatio, 1)} → NTU ≈ ${fmt(params.airToWaterRatio * 0.6, 1)} → ${fmt(size.h2sStripPct, 0)}% removal. Inlet H₂S of ${fmt(params.influentH2S, 2)} mg/L → outlet ${fmt(size.outletH2S, 3)} mg/L.`,
    },
    {
      title: 'Dissolved oxygen target',
      content: `DO saturation at ${params.temperature}°C ≈ ${fmt(14.6 - 0.394 * params.temperature + 0.0076 * params.temperature ** 2, 1)} mg/L. Column achieves ${fmt(size.doAchieved, 1)} mg/L. For Birm media downstream, DO must be ≥15% of Fe — here Fe ${fmt(params.influentFe, 2)} mg/L needs ≥${fmt(params.influentFe * 0.15, 2)} mg/L DO ✓ comfortably met.`,
    },
  ];

  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title="Aeration Column"
        subtitle="Pre-oxidation of Fe²⁺ / Mn²⁺ and H₂S stripping — feed conditioning for catalytic media or RO"
        icon={Wind}
        right={
          <div className="flex gap-2">
            <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          </div>
        }
      />

      <SampleSelector loader={sampleLoader} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* LEFT: Process flow */}
        <div className="xl:col-span-7 space-y-4">
          <div className="p-4 rb-card-sm">
            <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
              <ArrowDown className="w-3 h-3" /> Aeration Column — Fe²⁺ → Fe³⁺ Floc Formation
            </p>
            <svg viewBox="0 0 900 360" className="w-full h-auto">
              <defs>
                <linearGradient id="waterGradA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
                </linearGradient>
              </defs>
              <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">AERATION COLUMN — PRE-OXIDATION</text>

              {/* Influent */}
              <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradA)" stroke="#38bdf8" strokeWidth="2" />
              <text x="80" y="72" fill="#38bdf8" fontSize="9" textAnchor="middle" fontWeight="bold">Borehole In</text>
              <text x="80" y="86" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 1)} m³/h</text>
              <text x="80" y="98" fill="#f43f5e" fontSize="8" textAnchor="middle">Fe²⁺ {fmt(params.influentFe, 2)} mg/L</text>
              <text x="80" y="110" fill="#f43f5e" fontSize="8" textAnchor="middle">Mn²⁺ {fmt(params.influentMn, 2)} mg/L</text>
              <text x="80" y="122" fill="#94a3b8" fontSize="8" textAnchor="middle">pH {fmt(params.ph, 1)}</text>

              <line x1="132" y1="95" x2="280" y2="95" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />
              <polygon points="278,92 285,95 278,98" fill="#38bdf8" />
              <text x="200" y="86" fill="#38bdf8" fontSize="8" textAnchor="middle">water + dissolved Fe²⁺</text>

              {/* Column */}
              <rect x="290" y="30" width="180" height="220" rx="6" fill="url(#waterGradA)" stroke="#10b981" strokeWidth="2" />
              {/* Packing texture */}
              {Array.from({length: 8}).map((_, i) => (
                <line key={i} x1="295" y1={60 + i * 24} x2="465" y2={60 + i * 24} stroke="#10b981" strokeWidth="0.5" opacity="0.4" strokeDasharray="3 3" />
              ))}
              {Array.from({length: 30}).map((_, i) => (
                <circle key={i} cx={300 + (i * 37) % 160} cy={50 + Math.floor(i / 4) * 28 + (i % 2) * 10} r="2" fill="#10b981" opacity="0.3" />
              ))}

              <text x="380" y="50" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Packed Bed</text>
              <text x="380" y="265" fill="#10b981" fontSize="8" textAnchor="middle">Ø{fmt(params.columnDiameter, 2)} m · H {fmt(params.columnHeight, 1)} m</text>
              <text x="380" y="278" fill="#94a3b8" fontSize="8" textAnchor="middle">EBCT {fmt(size.ebct, 0)} s · v {fmt(size.superficialVelocity, 0)} m/h</text>

              {/* Air inlet */}
              <g transform="translate(330, 250)">
                <rect x="0" y="0" width="100" height="14" rx="3" fill="#0f172a" stroke="#fbbf24" strokeWidth="1.5" />
                <text x="50" y="10" fill="#fbbf24" fontSize="8" textAnchor="middle">Air sparger</text>
              </g>
              {/* Bubbles rising */}
              {Array.from({length: 12}).map((_, i) => (
                <circle key={`b${i}`} cx={310 + (i * 17) % 150} cy={235 - (i * 15) % 180} r={1.5 + (i % 3) * 0.8} fill="#fbbf24" opacity={0.4 + (i % 4) * 0.15} />
              ))}

              {/* Blower */}
              <g transform="translate(290, 295)">
                <rect x="0" y="0" width="60" height="35" rx="4" fill="#0f172a" stroke="#fbbf24" strokeWidth="1.5" />
                <text x="30" y="14" fill="#fbbf24" fontSize="8" textAnchor="middle">Blower</text>
                <text x="30" y="26" fill="#94a3b8" fontSize="7" textAnchor="middle">{fmt(size.airFlow, 0)} Nm³/h</text>
              </g>
              <text x="365" y="320" fill="#fbbf24" fontSize="8" textAnchor="middle">P ≈ {fmt(size.blowerPower, 2)} kW</text>

              {/* Off-gas vent */}
              <line x1="380" y1="30" x2="380" y2="10" stroke="#94a3b8" strokeWidth="1.5" />
              <text x="380" y="8" fill="#94a3b8" fontSize="7" textAnchor="middle">vent (CO₂, H₂S)</text>

              <line x1="470" y1="170" x2="510" y2="170" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
              <polygon points="508,167 515,170 508,173" fill="#10b981" />

              {/* Treated effluent */}
              <rect x="515" y="130" width="100" height="80" rx="6" fill="url(#waterGradA)" stroke="#10b981" strokeWidth="2" />
              <text x="565" y="148" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">To Catalytic Filter</text>
              <text x="565" y="162" fill="#94a3b8" fontSize="8" textAnchor="middle">DO {fmt(size.doAchieved, 1)} mg/L</text>
              <text x="565" y="174" fill="#f59e0b" fontSize="8" textAnchor="middle">Fe³⁺ floc {fmt(params.influentFe - size.outletFe, 2)} mg/L</text>
              <text x="565" y="186" fill="#94a3b8" fontSize="8" textAnchor="middle">Fe²⁺ rem {fmt(size.outletFe, 3)} mg/L</text>
              <text x="565" y="198" fill="#94a3b8" fontSize="8" textAnchor="middle">H₂S strip {fmt(size.h2sStripPct, 0)}%</text>

              {/* Performance KPIs */}
              <g transform="translate(640, 50)">
                <rect x="0" y="0" width="240" height="200" rx="6" fill="#0f172a" stroke="#1e293b" strokeWidth="1" />
                <text x="120" y="16" fill="#94a3b8" fontSize="9" textAnchor="middle" fontWeight="bold">PERFORMANCE</text>
                <text x="10" y="38" fill="#64748b" fontSize="8">Fe oxidation</text>
                <text x="230" y="38" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.feOxidationPct, 0)}%</text>
                <text x="10" y="58" fill="#64748b" fontSize="8">Mn oxidation</text>
                <text x="230" y="58" fill="#f59e0b" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.mnOxidationPct, 0)}%</text>
                <text x="10" y="78" fill="#64748b" fontSize="8">H₂S stripping</text>
                <text x="230" y="78" fill="#10b981" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.h2sStripPct, 0)}%</text>
                <text x="10" y="98" fill="#64748b" fontSize="8">DO achieved</text>
                <text x="230" y="98" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.doAchieved, 1)} mg/L</text>
                <text x="10" y="118" fill="#64748b" fontSize="8">EBCT</text>
                <text x="230" y="118" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.ebct, 0)} s</text>
                <text x="10" y="138" fill="#64748b" fontSize="8">Sup. velocity</text>
                <text x="230" y="138" fill="#06b6d4" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.superficialVelocity, 0)} m/h</text>
                <text x="10" y="158" fill="#64748b" fontSize="8">Air flow</text>
                <text x="230" y="158" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.airFlow, 0)} Nm³/h</text>
                <text x="10" y="178" fill="#64748b" fontSize="8">Blower power</text>
                <text x="230" y="178" fill="#fbbf24" fontSize="9" textAnchor="end" fontWeight="bold">{fmt(size.blowerPower, 2)} kW</text>
              </g>
            </svg>
          </div>
        </div>

        {/* RIGHT: Controls */}
        <div className="xl:col-span-5 space-y-5">
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>

          {/* Status */}
          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{size.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{size.statusDetail}</p>
          </div>

          {/* Water */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Water Quality In</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Flow Rate" value={params.flowRate} min={1} max={200} step={1} unit="m³/h" icon={Droplets} color="#38bdf8" onChange={v => update('flowRate', v)} />
              <Slider label="Influent Fe²⁺" value={params.influentFe} min={0} max={5} step={0.01} unit="mg/L" icon={FlaskConical} color="#f43f5e" onChange={v => update('influentFe', v)} />
              <Slider label="Influent Mn²⁺" value={params.influentMn} min={0} max={2} step={0.01} unit="mg/L" icon={FlaskConical} color="#a855f7" onChange={v => update('influentMn', v)} />
              <Slider label="Influent H₂S" value={params.influentH2S} min={0} max={10} step={0.05} unit="mg/L" icon={Beaker} color="#fbbf24" onChange={v => update('influentH2S', v)} />
              <Slider label="pH" value={params.ph} min={5} max={9.5} step={0.1} unit="" icon={Beaker} color="#a855f7" onChange={v => update('ph', v)} />
              <Slider label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={v => update('alkalinity', v)} />
              <Slider label="Temperature" value={params.temperature} min={2} max={30} step={1} unit="°C" icon={Thermometer} color="#06b6d4" onChange={v => update('temperature', v)} />
            </div>
          </div>

          {/* Column geometry */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Column Geometry &amp; Air</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Column Diameter" value={params.columnDiameter} min={0.3} max={3} step={0.05} unit="m" icon={Gauge} color="#10b981" onChange={v => update('columnDiameter', v)} />
              <Slider label="Packed Bed Height" value={params.columnHeight} min={0.5} max={6} step={0.1} unit="m" icon={ArrowDown} color="#10b981" onChange={v => update('columnHeight', v)} />
              <Slider label="Freeboard" value={params.freeboard} min={0.2} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#94a3b8" onChange={v => update('freeboard', v)} />
              <Slider label="Packing Size" value={params.packingSize} min={15} max={100} step={5} unit="mm" icon={Gauge} color="#f59e0b" onChange={v => update('packingSize', v)} info="Nominal packing OD" />
              <Slider label="Air : Water Ratio" value={params.airToWaterRatio} min={1} max={30} step={0.5} unit="Nm³/m³" icon={Wind} color="#fbbf24" onChange={v => update('airToWaterRatio', v)} info="Typical 5–15 for Fe; 15–30 for VOC strip" />
              <Slider label="Blower ΔP" value={params.airPressure} min={0.1} max={1.2} step={0.05} unit="bar" icon={TrendingUp} color="#fbbf24" onChange={v => update('airPressure', v)} />
            </div>
          </div>

          {/* Gauges */}
          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-200">Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <GaugeBar value={size.feOxidationPct} max={100} label="Fe oxidation" unit="%" color="#10b981" />
              <GaugeBar value={size.doAchieved} max={12} label="DO achieved" unit="mg/L" color="#06b6d4" />
              <GaugeBar value={size.ebct} max={120} label="EBCT" unit="s" color="#38bdf8" />
              <GaugeBar value={size.h2sStripPct} max={100} label="H₂S strip" unit="%" color="#fbbf24" />
            </div>
          </div>

          {/* Sizing */}
          <div className="p-4 rb-inner-card space-y-2">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-emerald-400" /> Sizing</h3>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Column area</span><span className="text-rb-brand font-bold">{fmt(size.area, 2)} m²</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Packing volume</span><span className="text-rb-brand font-bold">{fmt(size.packingVolume, 2)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Outlet Fe²⁺ (residual dissolved)</span><span className="text-rb-brand font-bold">{fmt(size.outletFe, 3)} mg/L</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Outlet Mn²⁺</span><span className="text-rb-brand font-bold">{fmt(size.outletMn, 3)} mg/L</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Outlet H₂S</span><span className="text-rb-brand font-bold">{fmt(size.outletH2S, 3)} mg/L</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Air flow</span><span className="text-rb-brand font-bold">{fmt(size.airFlow, 0)} Nm³/h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Blower power (adiabatic ~65%)</span><span className="text-rb-brand font-bold">{fmt(size.blowerPower, 2)} kW</span></div>
            </div>
          </div>

          {/* Notes */}
          <div className="p-4 rb-card space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes</h3>
            <div className="space-y-2 text-xs">
              {size.feOxidationPct < 90 && (
                <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">⚠ Fe oxidation {fmt(size.feOxidationPct, 0)}% — supplement with NaOCl trim (0.5–1 mg/L) or KMnO₄ if pH is below 7.</div>
              )}
              {params.influentMn > 0.1 && size.mnOxidationPct < 50 && (
                <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">⚠ Mn at {fmt(params.influentMn, 2)} mg/L requires catalytic media downstream (Greensand Plus + KMnO₄, or pyrolusite).</div>
              )}
              {size.doAchieved < params.influentFe * 0.15 && (
                <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠ DO insufficient for Birm downstream (needs ≥15% of Fe). Increase air ratio.</div>
              )}
              <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-emerald-400">ℹ Tellerette packing with 8:1 G/L gives reliable Fe oxidation at pH 7.5+. Pre-oxidised water then passes to catalytic media or coagulation.</div>
            </div>
          </div>

          {/* Science */}
          <div className="p-4 rb-card space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Beaker className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <div className="space-y-3">
              {scienceSections.map((s, i) => (
                <details key={i} className="rounded-lg border border-slate-800 bg-rb-elevated/40">
                  <summary className="cursor-pointer p-3 text-xs font-bold text-sky-400 hover:text-sky-300">{s.title}</summary>
                  <p className="px-3 pb-3 text-[11px] text-slate-300 leading-relaxed">{s.content}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
